import { Button, Modal, RadioGroup, RadioItem, useToast } from '@ntconsult/aluma-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SpreadsheetExport } from '../hooks/useSpreadsheetExport';
import type { ExportFormat, ExportScope, ScopedExportRequest } from '../types/Filters';
import { i18nKeyForError, parseApiError } from '../utils/errors';

export type ExportModalLabels = {
  title: string;
  /** Substitui a descrição padrão (filtros, ordenação e colunas da tabela). */
  description?: string;
} & (
  | { allPages: string; currentPage: string }
  /** Tela sem paginação (Síntese): sem escolha de conteúdo, o arquivo leva tudo. */
  | { allPages?: never; currentPage?: never }
);

type ExportModalProps = {
  open: boolean;
  onClose: () => void;
  exportMutation: SpreadsheetExport<ScopedExportRequest>;
  labels: ExportModalLabels;
};

function FormBody({ onClose, exportMutation, labels }: Omit<ExportModalProps, 'open'>) {
  const { t } = useTranslation(['alocacao', 'common']);
  const { toast } = useToast();
  const [scope, setScope] = useState<ExportScope>('all');
  const [format, setFormat] = useState<ExportFormat>('xlsx');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        exportMutation.mutate(
          { scope, format },
          {
            onSuccess: () => {
              toast.success({ title: t('alocacao:exportacao.sucesso') });
              onClose();
            },
            onError: (error) => {
              const apiError = parseApiError(error);
              toast.danger({
                title: t(
                  (apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never,
                ),
              });
            },
          },
        );
      }}
    >
      <Modal.Body className="flex flex-col gap-6">
        {labels.currentPage !== undefined && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium text-foreground">
              {t('alocacao:exportacao.conteudo')}
            </legend>
            <RadioGroup
              name="export-scope"
              value={scope}
              onValueChange={(value) => setScope(value as ExportScope)}
            >
              <RadioItem value="all" label={labels.allPages} />
              <RadioItem value="page" label={labels.currentPage} />
            </RadioGroup>
          </fieldset>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-foreground">
            {t('alocacao:exportacao.formato')}
          </legend>
          <RadioGroup
            name="export-format"
            orientation="horizontal"
            value={format}
            onValueChange={(value) => setFormat(value as ExportFormat)}
          >
            <RadioItem value="xlsx" label={t('alocacao:exportacao.excel')} />
            <RadioItem value="csv" label={t('alocacao:exportacao.csv')} />
          </RadioGroup>
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={exportMutation.isPending}>
          {t('alocacao:exportacao.botao')}
        </Button>
      </Modal.Footer>
    </form>
  );
}

/** Filtros, buscas, ordenação e colunas visíveis valem nas duas opções; "só esta página" leva também a página e o tamanho dela. */
export function ExportModal({ open, onClose, ...props }: ExportModalProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy={titleId} size="sm">
      <Modal.Header
        titleId={titleId}
        title={props.labels.title}
        description={props.labels.description ?? t('alocacao:exportacao.descricao')}
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      {open && <FormBody onClose={onClose} {...props} />}
    </Modal>
  );
}
